/**
 * /employees — list + create + edit personnel records.
 *
 * Layer 1 employee admin. Each employee is a Party (party_type=employee)
 * with a payroll-extension row carrying pay_rate, burden_pct, and the
 * defaults used to pre-fill time entries. Most operators just need the
 * list; the form dialog handles create + update.
 *
 * Sensitive: pay_rate + tax_id are visible to anyone with read access
 * to /personnel. Lock down via the AppShell role guard if that's an
 * issue (currently any authenticated user can read; only accountant+
 * can write).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { Box, Chip } from "@mui/material";

import { api } from "@/lib/api";
import { FlexGrid, gridDefaults } from "@/grids/gridDefaults";
import { employeesListQueryOptions, personnelKeys } from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { EmployeeFormDialog } from "@/components/EmployeeFormDialog";
import type { Employee, EmploymentStatus } from "@/lib/types";

export const Route = createFileRoute("/_auth/employees")({
  component: EmployeesPage,
});

const STATUS_COLOR: Record<EmploymentStatus, "success" | "warning" | "default"> = {
  active: "success",
  on_leave: "warning",
  terminated: "default",
};

function EmployeesPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [showTerminated, setShowTerminated] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Employee | null>(null);

  const employeesQuery = useQuery(
    employeesListQueryOptions({ include_terminated: showTerminated }),
  );

  const terminate = useMutation({
    mutationFn: async (vars: { id: string; date: string }) =>
      api(
        `/api/personnel/employees/${vars.id}/terminate?termination_date=${vars.date}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: personnelKeys.all });
      toast.success("Employee terminated");
    },
    onError: (err: Error) => toast.error(`Terminate failed: ${err.message}`),
  });

  const requestTerminate = (e: Employee) => {
    const today = new Date().toISOString().slice(0, 10);
    const d = window.prompt(
      `Terminate ${e.name}? Last day of employment (YYYY-MM-DD):`,
      today,
    );
    if (d) terminate.mutate({ id: e.id, date: d });
  };

  const columns = useMemo<GridColDef<Employee>[]>(() => [
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", width: 220 },
    {
      field: "employment_status",
      headerName: "Status",
      width: 110,
      renderCell: (p: GridRenderCellParams<Employee>) => (
        <Chip
          label={p.row.employment_status.replace("_", " ")}
          size="small"
          color={STATUS_COLOR[p.row.employment_status]}
          sx={{ fontSize: 10, height: 20 }}
        />
      ),
    },
    {
      field: "pay_rate",
      headerName: "Rate",
      width: 110,
      renderCell: (p) => (
        <span className="font-mono text-sm">
          ${Number(p.row.pay_rate).toLocaleString(undefined, {
            minimumFractionDigits: 2, maximumFractionDigits: 2,
          })}
          <span className="text-xs text-slate-500 dark:text-slate-400">/hr</span>
        </span>
      ),
    },
    {
      field: "pay_frequency",
      headerName: "Pays",
      width: 110,
    },
    {
      field: "burden_pct",
      headerName: "Burden",
      width: 90,
      renderCell: (p) => {
        const pct = Number(p.row.burden_pct) * 100;
        return pct > 0
          ? <span className="text-amber-700">{pct.toFixed(1)}%</span>
          : <span className="text-slate-400 dark:text-slate-500">—</span>;
      },
    },
    {
      // Layer 1.6 — PTO balances at a glance. Format "vac/sick/other".
      // Negative = over-spent; red. Zero = dimmed dash.
      field: "_pto_balances",
      headerName: "PTO (V/S/O)",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<Employee>) => {
        const fmt = (v: string) => {
          const n = Number(v);
          if (n === 0) return <span className="text-slate-400 dark:text-slate-500">—</span>;
          const cls = n < 0 ? "text-red-600" : "text-slate-700";
          return <span className={cls}>{n.toFixed(1)}</span>;
        };
        return (
          <span
            className="font-mono text-xs flex items-center gap-1"
            title={
              `Vacation: ${p.row.vacation_balance}h | ` +
              `Sick: ${p.row.sick_balance}h | ` +
              `Other PTO: ${p.row.other_pto_balance}h | ` +
              `Year resets: month ${p.row.pto_year_start_month}`
            }
          >
            {fmt(p.row.vacation_balance)}
            <span className="text-slate-300">/</span>
            {fmt(p.row.sick_balance)}
            <span className="text-slate-300">/</span>
            {fmt(p.row.other_pto_balance)}
          </span>
        );
      },
    },
    {
      // Layer 1.6 — auto-OT rule indicator. Plain text for non-manual.
      field: "ot_calculation_rule",
      headerName: "OT rule",
      width: 110,
      renderCell: (p: GridRenderCellParams<Employee>) => {
        if (p.row.ot_calculation_rule === "manual") {
          return <span className="text-xs text-slate-400 dark:text-slate-500">manual</span>;
        }
        const label =
          p.row.ot_calculation_rule === "federal_weekly" ? "Fed >40/wk" :
          p.row.ot_calculation_rule === "ca_daily" ? "CA >8/day" :
          p.row.ot_calculation_rule;
        return (
          <span
            className="text-xs text-amber-700"
            title="Time entries auto-flag overtime per this rule."
          >
            ⚡ {label}
          </span>
        );
      },
    },
    {
      field: "is_salaried",
      headerName: "Salaried",
      width: 90,
      renderCell: (p) => p.row.is_salaried ? "✓" : "—",
    },
    {
      field: "default_cost_type",
      headerName: "Default cost",
      width: 130,
      renderCell: (p) => (
        <span className="text-xs text-slate-600 dark:text-slate-300">
          {p.row.default_cost_type}
        </span>
      ),
    },
    {
      field: "hire_date",
      headerName: "Hired",
      width: 110,
    },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "_actions",
      headerName: "",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<Employee>) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setEditTarget(p.row); setFormOpen(true); }}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Edit
          </button>
          {p.row.employment_status !== "terminated" && (
            <button
              onClick={() => requestTerminate(p.row)}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Terminate
            </button>
          )}
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Employees</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {employeesQuery.data?.length ?? 0} employee
            {employeesQuery.data?.length === 1 ? "" : "s"}
          </span>
          <div className="flex-1" />
          <label className="flex items-center gap-1 text-xs text-slate-600 cursor-pointer dark:text-slate-300">
            <input
              type="checkbox"
              checked={showTerminated}
              onChange={(e) => setShowTerminated(e.target.checked)}
            />
            Show terminated
          </label>
          <button
            onClick={() => { setEditTarget(null); setFormOpen(true); }}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New employee
          </button>
        </div>
      </div>

      <EmployeeFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditTarget(null); }}
        target={editTarget}
      />

      <div className="flex-1 min-h-0 px-6 py-4">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={employeesQuery.data ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            loading={employeesQuery.isLoading}
            density="compact"
            disableRowSelectionOnClick
          />
        </FlexGrid>
      </div>
    </div>
  );
}
